import React, { useState, useEffect } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { 
  AdminPanelSettings as AdminIcon,
  Check as CheckIcon,
  Visibility,
  VisibilityOff,
  Person as PersonIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import axios from 'axios';
import SetupLayout from './SetupLayout';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

const AdminUserSetup: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const signupData = JSON.parse(sessionStorage.getItem('signupData') || '{}');
  const selectedPlan = signupData.selectedPlan || { name: 'Free' };
  const domain = signupData.domain || 'example.com';

  useEffect(() => {
    const storedData = JSON.parse(sessionStorage.getItem('signupData') || '{}');
    if (!storedData.organization) {
      navigate('/signup');
    }
  }, [navigate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const validateForm = () => {
    if (!formData.firstName.trim()) return 'First name is required';
    if (!formData.lastName.trim()) return 'Last name is required';
    if (!formData.email.trim()) return 'Email username is required';
    if (formData.password.length < 8) return 'Password must be at least 8 characters';
    if (formData.password !== formData.confirmPassword) return 'Passwords do not match';
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Read fresh data from sessionStorage
      const currentData = JSON.parse(sessionStorage.getItem('signupData') || '{}');
      const currentDomain = currentData.domain || domain;
      
      const response = await axios.post(`${API_URL}/signup/create-admin-user`, {
        organizationId: currentData.organizationId,
        domainId: currentData.domainId,
        firstName: formData.firstName,
        lastName: formData.lastName,
        username: formData.email,  // Just the local part, backend appends @domain
        password: formData.password,
        token: currentData.token,
      });

      if (response.data.success || response.data.data) {
        setSuccess('Admin account created successfully!');
        sessionStorage.setItem('signupData', JSON.stringify({
          ...currentData,
          adminEmail: `${formData.email}@${currentDomain}`,
          adminFirstName: formData.firstName,
          adminLastName: formData.lastName,
        }));
        setTimeout(() => navigate('/setup/dns'), 1000);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create admin account');
    } finally {
      setLoading(false);
    }
  };

  const passwordStrength = () => {
    const p = formData.password;
    if (!p) return { strength: 0, label: '' };
    let score = 0;
    if (p.length >= 8) score++;
    if (p.length >= 12) score++;
    if (/[A-Z]/.test(p)) score++;
    if (/[a-z]/.test(p)) score++;
    if (/[0-9]/.test(p)) score++;
    if (/[^A-Za-z0-9]/.test(p)) score++;
    if (score <= 2) return { strength: 33, label: 'Weak', color: '#ef4444' };
    if (score <= 4) return { strength: 66, label: 'Good', color: '#f59e0b' };
    return { strength: 100, label: 'Strong', color: '#22c55e' };
  };

  const strength = passwordStrength();

  return (
    <SetupLayout activeStep={4} backPath="/setup/domain">
      {/* Main Content */}
      <Box sx={{ flex: 1, display: 'flex', py: 6 }}>
        <Box sx={{ maxWidth: 1200, mx: 'auto', width: '100%', px: 3, display: 'flex', gap: 6 }}>
          {/* Left - Form */}
          <Box sx={{ flex: 1, maxWidth: 500 }}>
            <Box sx={{ width: 60, height: 60, borderRadius: '50%', bgcolor: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
              <AdminIcon sx={{ fontSize: 30, color: '#dc2626' }} />
            </Box>

            <Typography variant="h4" sx={{ fontWeight: 700, color: '#111827', mb: 1, fontFamily: 'Inter, sans-serif' }}>
              Create admin account
            </Typography>
            <Typography sx={{ color: '#6b7280', mb: 4, fontFamily: 'Inter, sans-serif' }}>
              Set up your administrator email account
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>{success}</Alert>}

            <form onSubmit={handleSubmit}>
              <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                <TextField
                  fullWidth
                  name="firstName"
                  placeholder="First name"
                  value={formData.firstName}
                  onChange={handleChange}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><PersonIcon sx={{ color: '#9ca3af' }} /></InputAdornment>,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2, bgcolor: '#f9fafb',
                      '& fieldset': { borderColor: '#e5e7eb' },
                      '&:hover fieldset': { borderColor: '#dc2626' },
                      '&.Mui-focused fieldset': { borderColor: '#dc2626' },
                    },
                  }}
                />
                <TextField
                  fullWidth
                  name="lastName"
                  placeholder="Last name"
                  value={formData.lastName}
                  onChange={handleChange}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2, bgcolor: '#f9fafb',
                      '& fieldset': { borderColor: '#e5e7eb' },
                      '&:hover fieldset': { borderColor: '#dc2626' },
                      '&.Mui-focused fieldset': { borderColor: '#dc2626' },
                    },
                  }}
                />
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography sx={{ fontSize: '0.875rem', color: '#6b7280', mb: 1, fontFamily: 'Inter, sans-serif' }}>
                  Email address
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <TextField
                    name="email"
                    placeholder="admin"
                    value={formData.email}
                    onChange={handleChange}
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><EmailIcon sx={{ color: '#9ca3af' }} /></InputAdornment>,
                    }}
                    sx={{
                      flex: 1,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px 0 0 8px', bgcolor: '#f9fafb',
                        '& fieldset': { borderColor: '#e5e7eb' },
                        '&:hover fieldset': { borderColor: '#dc2626' },
                        '&.Mui-focused fieldset': { borderColor: '#dc2626' },
                      },
                    }}
                  />
                  <Box sx={{ px: 2, py: 1.85, bgcolor: '#f3f4f6', border: '1px solid #e5e7eb', borderLeft: 'none', borderRadius: '0 8px 8px 0' }}>
                    <Typography sx={{ color: '#6b7280', fontWeight: 500, fontFamily: 'Inter, sans-serif' }}>
                      @{domain}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <TextField
                fullWidth
                type={showPassword ? 'text' : 'password'}
                name="password"
                placeholder="Password"
                value={formData.password}
                onChange={handleChange}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mb: 1,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2, bgcolor: '#f9fafb',
                    '& fieldset': { borderColor: '#e5e7eb' },
                    '&:hover fieldset': { borderColor: '#dc2626' },
                    '&.Mui-focused fieldset': { borderColor: '#dc2626' },
                  },
                }}
              />
              
              {formData.password && (
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ height: 4, bgcolor: '#e5e7eb', borderRadius: 2, overflow: 'hidden' }}>
                    <Box sx={{ height: '100%', width: `${strength.strength}%`, bgcolor: strength.color, transition: 'all 0.3s' }} />
                  </Box>
                  <Typography sx={{ fontSize: '0.75rem', color: strength.color, mt: 0.5, fontFamily: 'Inter, sans-serif' }}>
                    {strength.label}
                  </Typography>
                </Box>
              )}

              <TextField
                fullWidth
                type={showConfirmPassword ? 'text' : 'password'}
                name="confirmPassword"
                placeholder="Confirm password"
                value={formData.confirmPassword}
                onChange={handleChange}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end">
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2, bgcolor: '#f9fafb',
                    '& fieldset': { borderColor: '#e5e7eb' },
                    '&:hover fieldset': { borderColor: '#dc2626' },
                    '&.Mui-focused fieldset': { borderColor: '#dc2626' },
                  },
                }}
              />

              <Button
                fullWidth
                type="submit"
                variant="contained"
                disabled={loading}
                sx={{ py: 1.5, bgcolor: '#dc2626', '&:hover': { bgcolor: '#b91c1c' }, fontWeight: 600, fontSize: '1rem', textTransform: 'none', borderRadius: 2 }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Create Admin Account'}
              </Button>
            </form>
          </Box>

          {/* Right - Info Panel */}
          <Box sx={{ width: 350, display: { xs: 'none', md: 'block' } }}>
            <Box sx={{ bgcolor: '#f8fafc', borderRadius: 3, p: 4, border: '1px solid #e5e7eb' }}>
              <Typography sx={{ fontWeight: 600, color: '#111827', mb: 3, fontFamily: 'Inter, sans-serif' }}>
                Admin account features
              </Typography>
              
              {[
                { title: 'Full Control', desc: 'Manage all organization settings' },
                { title: 'User Management', desc: 'Add, edit, and remove users' },
                { title: 'Domain Settings', desc: 'Configure DNS and email routing' },
                { title: 'Security', desc: 'Set policies and monitor activity' },
              ].map((item, i) => (
                <Box key={i} sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, mt: 0.5 }}>
                    <CheckIcon sx={{ fontSize: 14, color: '#22c55e' }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 600, color: '#111827', fontSize: '0.875rem', fontFamily: 'Inter, sans-serif' }}>
                      {item.title}
                    </Typography>
                    <Typography sx={{ color: '#6b7280', fontSize: '0.8rem', fontFamily: 'Inter, sans-serif' }}>
                      {item.desc}
                    </Typography>
                  </Box>
                </Box>
              ))}

              <Box sx={{ mt: 4, pt: 4, borderTop: '1px solid #e5e7eb' }}>
                <Typography sx={{ color: '#6b7280', fontSize: '0.75rem', fontFamily: 'Inter, sans-serif' }}>
                  Organization
                </Typography>
                <Typography sx={{ fontWeight: 700, color: '#111827', fontFamily: 'Inter, sans-serif' }}>
                  {signupData.organization}
                </Typography>
                <Typography sx={{ color: '#6b7280', fontSize: '0.75rem', mt: 2, fontFamily: 'Inter, sans-serif' }}>
                  Domain
                </Typography>
                <Typography sx={{ fontWeight: 700, color: '#111827', fontFamily: 'Inter, sans-serif' }}>
                  {domain}
                </Typography>
                <Typography sx={{ color: '#6b7280', fontSize: '0.75rem', mt: 2, fontFamily: 'Inter, sans-serif' }}>
                  Plan
                </Typography>
                <Typography sx={{ fontWeight: 700, color: '#111827', fontFamily: 'Inter, sans-serif' }}>
                  {selectedPlan.name}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </SetupLayout>
  );
};

export default AdminUserSetup;
